import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { ArrowBack, CheckCircle } from "@mui/icons-material";
import { toast } from "react-toastify";
import attendanceService from "../../services/attendanceService";
import { formatTimeRange12h } from "../../utils/timetableTimeSlots";

const toDateString = (value) => {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  const y = safe.getFullYear();
  const m = String(safe.getMonth() + 1).padStart(2, "0");
  const day = String(safe.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const hasValidGeo = (geo) =>
  geo &&
  geo.lat != null &&
  geo.lng != null &&
  Number.isFinite(Number(geo.lat)) &&
  Number.isFinite(Number(geo.lng));

const resolveGeo = async () => {
  if (!("geolocation" in navigator)) {
    return { lat: null, lng: null, error: "Geolocation is not supported in this browser." };
  }
  const tryGet = (options) =>
    new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (error) => resolve({ error }),
        options
      );
    });
  const strategies = [
    { enableHighAccuracy: false, timeout: 15000, maximumAge: 120000 },
    { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
  ];
  let lastError = null;
  for (const options of strategies) {
    const result = await tryGet(options);
    if (hasValidGeo(result)) return { lat: Number(result.lat), lng: Number(result.lng) };
    if (result?.error) lastError = result.error;
  }
  const msg =
    lastError?.code === 1
      ? "Location permission denied. Allow location for this site and try again."
      : "Unable to read GPS coordinates. Enable location services and try again.";
  return { lat: null, lng: null, error: msg };
};

const normalizeStatus = (raw) => String(raw || "").toUpperCase();

const studentDisplayName = (s) =>
  s?.fullName ||
  s?.user?.fullName ||
  s?.name ||
  s?.studentName ||
  "";

const studentDisplayId = (s) =>
  s?.studentLoginID ||
  s?.rollNo ||
  s?.rollNumber ||
  "";

const ScheduleManualAttendance = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const schedule = location.state?.schedule || null;
  const sessionDate = useMemo(() => toDateString(schedule?.date || new Date()), [schedule?.date]);
  const classId = schedule?.classId ? Number(schedule.classId) : null;
  const subjectId = schedule?.subjectId ? Number(schedule.subjectId) : null;
  const timeTableSlotId = schedule?.timeTableSlotId ? Number(schedule.timeTableSlotId) : null;
  const practicalBatchCode = schedule?.practicalBatchCode || null;
  const sessionKind =
    schedule?.sessionKind ||
    (practicalBatchCode ? "PRACTICAL" : null);
  const lectureType = schedule?.lectureType || null;

  const rosterQueryExtras = useMemo(
    () => ({
      ...(sessionKind ? { sessionKind } : {}),
      ...(practicalBatchCode ? { practicalBatchCode } : {}),
      ...(lectureType ? { lectureType } : {}),
    }),
    [sessionKind, practicalBatchCode, lectureType]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [roster, setRoster] = useState({ attendance: [], remaining: [], onLeave: [] });
  const [manualStatus, setManualStatus] = useState({});

  const allRows = useMemo(
    () => [...roster.attendance, ...roster.remaining, ...roster.onLeave],
    [roster.attendance, roster.remaining, roster.onLeave]
  );

const buildManualStatusFromRoster = (attendance, remaining, onLeave) => {
  const next = {};
  attendance.forEach((s) => {
    const raw = normalizeStatus(s?.attendance?.status);
    if (!raw) return;
    next[s.id] = raw === "ON_LEAVE" ? "LEAVE" : raw;
  });
  onLeave.forEach((s) => {
    if (next[s.id]) return;
    const raw = normalizeStatus(s?.attendance?.status);
    if (raw && raw !== "PRESENT") {
      next[s.id] = raw === "ON_LEAVE" ? "LEAVE" : raw;
    } else if (s?.leave) {
      next[s.id] = "LEAVE";
    }
  });
  remaining.forEach((s) => {
    if (!next[s.id]) {
      // Leave unmarked students without a default selection.
    }
  });
  return next;
};

  const fetchRoster = async (activeSessionId) => {
    if (!classId) return;
    const resolvedSessionId = activeSessionId || sessionId;
    const res = await attendanceService.getRoster(classId, {
      date: sessionDate,
      subjectId: subjectId || undefined,
      timeTableSlotId: timeTableSlotId || undefined,
      sessionId: resolvedSessionId || undefined,
      ...rosterQueryExtras,
      page: 1,
      limit: 500,
    });
    const data = res?.data?.data?.data || {};
    const attendance = data.attendance || [];
    const remaining = data.remaining || [];
    const onLeave = data.onLeave || [];
    setRoster({ attendance, remaining, onLeave });
    setManualStatus(buildManualStatusFromRoster(attendance, remaining, onLeave));
  };

  const startSession = async () => {
    if (!classId) return null;
    const geo = await resolveGeo();
    if (!hasValidGeo(geo)) {
      setError(geo?.error || "Location is required to start attendance.");
      return;
    }
    const payload = {
      classId,
      subjectId: subjectId || undefined,
      timeTableSlotId: timeTableSlotId || undefined,
      durationMinutes: 10,
      sessionDate,
      lat: geo.lat,
      lng: geo.lng,
      ...rosterQueryExtras,
    };
    try {
      const res = await attendanceService.startManualSession(payload);
      const data = res?.data?.data || {};
      if (!data?.sessionId) throw new Error("Manual session not created");
      setSessionId(data.sessionId);
      return data.sessionId;
    } catch (e) {
      // If a QR/OTP/manual session is already active, reuse it for manual marking.
      const existingSessionId =
        e?.response?.data?.data?.sessionId ||
        e?.response?.data?.sessionId ||
        null;

      if (existingSessionId) {
        setSessionId(existingSessionId);
        toast.info("Using existing active attendance session");
        return existingSessionId;
      }

      const msg = String(e?.response?.data?.message || e?.message || "").toLowerCase();
      if (msg.includes("already active") || msg.includes("already") || msg.includes("active session")) {
        const activeRes = await attendanceService.getActiveSession(classId);
        const activeData = activeRes?.data?.data || {};
        const sid = activeData?.sessionId || activeData?.id || null;
        if (sid) {
          setSessionId(sid);
          toast.info("Using existing active attendance session");
          return sid;
        }
      }

      throw e;
    }
    return null;
  };

  const restart = async () => {
    try {
      setLoading(true);
      setError("");
      setSessionId(null);
      setRoster({ attendance: [], remaining: [], onLeave: [] });
      setManualStatus({});
      const startedSessionId = await startSession();
      await fetchRoster(startedSessionId);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to restart manual attendance";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!schedule || !classId) {
      navigate(-1);
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setError("");
        const startedSessionId = await startSession();
        await fetchRoster(startedSessionId);
      } catch (e) {
        const msg = e?.response?.data?.message || e?.message || "Failed to start manual attendance";
        setError(msg);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule?.classId, schedule?.subjectId, schedule?.date]);

  const submit = async () => {
    if (!sessionId) {
      setError("No active manual session found");
      return;
    }
    const unmarked = allRows.filter((s) => !manualStatus[s.id]);
    if (unmarked.length > 0) {
      setError("Please select attendance status for all students before submitting.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const attendances = allRows.map((s) => ({
        studentId: s.id,
        status: manualStatus[s.id],
      }));
      await attendanceService.markBulkAttendance({
        sessionId,
        classId,
        subjectId,
        timeTableSlotId,
        attendances,
      });
      await fetchRoster(sessionId);
      toast.success("Attendance marked successfully");
      navigate(-1);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Failed to submit manual attendance";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button startIcon={<ArrowBack />} variant="text" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Manual Attendance
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Mark attendance for the selected schedule
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="outlined" onClick={restart} disabled={loading}>
            Restart Manual Session
          </Button>
          {loading && <CircularProgress size={22} />}
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {schedule?.className || `Class ${schedule?.classId || "-"}`}
              {schedule?.classSection ? ` - ${schedule.classSection}` : ""}
            </Typography>
            <Chip size="small" color="info" label={schedule?.subjectName || `Subject ${schedule?.subjectId || "-"}`} />
            <Chip size="small" label={`Date: ${sessionDate}`} />
            {schedule?.startTime && schedule?.endTime ? (
              <Chip size="small" label={formatTimeRange12h(schedule.startTime, schedule.endTime)} />
            ) : null}
            {practicalBatchCode ? (
              <Chip size="small" color="secondary" label={`Batch: ${practicalBatchCode}`} />
            ) : null}
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell align="right">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allRows.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {studentDisplayName(s) || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {studentDisplayId(s) ? `ID: ${studentDisplayId(s)}` : ""}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <FormControl>
                        <RadioGroup
                          row
                          value={manualStatus[s.id] ?? ""}
                          onChange={(e) =>
                            setManualStatus((p) => ({ ...p, [s.id]: normalizeStatus(e.target.value) }))
                          }
                        >
                          <FormControlLabel value="PRESENT" control={<Radio size="small" />} label="Present" />
                          <FormControlLabel value="ABSENT" control={<Radio size="small" />} label="Absent" />
                          <FormControlLabel value="LEAVE" control={<Radio size="small" />} label="On Leave" />
                        </RadioGroup>
                      </FormControl>
                    </TableCell>
                  </TableRow>
                ))}

                {allRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        No students found for this schedule.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<CheckCircle />}
              onClick={submit}
              disabled={loading || !sessionId}
            >
              Submit
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ScheduleManualAttendance;

